/**
 * 菜单太多的时候需要使用的菜单过滤器
 * 方便快速查找菜单
 * @author Zhang P
 * @date 2025-01-06
 */

import React, { useRef } from "react";
import { Search } from "@gw/web-basic-components";
import ClearIcon from "./clearIcon";
import SearchIcon from "./searchIcon";

type OptionValue = { label: string; value: string };

export interface MenuFilterProps {
  options: OptionValue[];
  onSelect?: (value: string) => void;
  /** 是否菜单折叠 */
  collapse?: boolean;
}

const MenuFilter: React.FC<MenuFilterProps> = (props) => {
  const { options, onSelect, collapse } = props;

  /** 搜索值 */
  const [searchValue, setSearchValue] = React.useState("");
  /** 处理中文输入完成状态之后生效筛选 */
  const isComposing = React.useRef(false);
  /** 一次搜索是否完成，用于筛选内容重置 */
  const isCompleted = React.useRef(false);

  const dom = useRef<HTMLDivElement>(null);

  const clearIcon = (
    <ClearIcon className="wbuc-text-[var(--gw-wbuc-appearance-menu-filter-text-color)]" />
  );

  const notFoundContent = (
    <span className="wbuc-text-[var(--gw-wbuc-appearance-menu-filter-text-color)]">
      无匹配结果
    </span>
  );

  const handleFilterOptions = (input: string, option: OptionValue) => {
    /** 输入法正在输入的时候，不过滤内容 */
    if (isComposing.current) return true;
    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = (event: React.CompositionEvent) => {
    isComposing.current = false;
    setSearchValue(event.data);
  };

  const handleSelect = (val) => {
    /** 完成一次有效操作 */
    isCompleted.current = true;
    onSelect?.(val);
  };

  const handleChange = (val: string | undefined) => {
    if (!isComposing.current) {
      setSearchValue(val);
    }
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && isCompleted.current) {
      /** 重置完成一次有效操作 */
      isCompleted.current = false;
      setSearchValue("");
    }
  };

  if (collapse) {
    return (
      <div
        className="wbuc-appearance-menu-filter wbuc-p-2.5 wbuc-text-[var(--gw-wbuc-appearance-menu-filter-text-color)]"
        ref={dom}
      >
        <div className="wbuc-flex wbuc-h-[2.875rem] wbuc-items-center wbuc-justify-center">
          <SearchIcon />
        </div>
      </div>
    );
  }

  return (
    <div className="wbuc-appearance-menu-filter wbuc-p-2.5" ref={dom}>
      <Search
        size="middle"
        showSearch
        placeholder="搜索"
        allowClear
        clearIcon={clearIcon}
        notFoundContent={notFoundContent}
        showArrow={false}
        searchValue={searchValue}
        options={options}
        filterOption={handleFilterOptions}
        getPopupContainer={() => dom.current}
        onSelect={handleSelect}
        onChange={handleChange}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        // @ts-ignore
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
      ></Search>
    </div>
  );
};

export default MenuFilter;
